import { z } from 'zod';

export const ByMetricTrendsGroupByConfiguration = z
  .object({
    key: z.string(),
    displayName: z.string(),
  })
  .describe('ByMetricTrendsGroupByConfiguration');

export type ByMetricTrendsGroupByConfiguration = z.infer<
  typeof ByMetricTrendsGroupByConfiguration
>;

export const ByMetricTrendsMetricBucketingStrategyType = z
  .enum(['accumulating', 'pointInTime'])
  .describe('ByMetricTrendsMetricBucketingStrategyType');

export type ByMetricTrendsMetricBucketingStrategyType = z.infer<
  typeof ByMetricTrendsMetricBucketingStrategyType
>;

export const ByMetricTrendsMetricBucketingStrategyTypes = Object.values(
  ByMetricTrendsMetricBucketingStrategyType.Values,
);

export const ByMetricTrendsMetricBucketingStrategyTypeToLabel: Record<
  ByMetricTrendsMetricBucketingStrategyType,
  string
> = {
  accumulating: 'Accumulating',
  pointInTime: 'Point in Time',
};

export const byMetricTrendsMetricBucketingStrategyDropdownValues: {
  optionLabel: string;
  optionValue: ByMetricTrendsMetricBucketingStrategyType;
}[] = ByMetricTrendsMetricBucketingStrategyTypes.map((type) => {
  return {
    optionLabel: ByMetricTrendsMetricBucketingStrategyTypeToLabel[type],
    optionValue: type,
  };
});

/**
 * The reason that the bucketing strategy types are not just literals
 * is because we want the type here to be in sync with a list of enum
 * values that we can use in a dropdown.
 * This allows us to have a single source of truth, but in order to the types
 * to be compatible with the dropdown, we need to use an intersection
 * with the enum type and the literal value.
 */
export const ByMetricTrendsMetricBucketingStrategyIntersectingType = <
  Type extends ByMetricTrendsMetricBucketingStrategyType,
>(
  literal: Type,
) => {
  return z
    .intersection(ByMetricTrendsMetricBucketingStrategyType, z.literal(literal))
    .describe('ByMetricTrendsMetricBucketingStrategyIntersectingType');
};

export const ByMetricTrendsMetricAccumulatingBucketingStrategy = z
  .object({
    type: ByMetricTrendsMetricBucketingStrategyIntersectingType('accumulating'),
    startDateColumn: z.string(),
    endDateColumn: z.string(),
  })
  .describe('ByMetricTrendsMetricAccumulatingBucketingStrategy');

export type ByMetricTrendsMetricAccumulatingBucketingStrategy = z.infer<
  typeof ByMetricTrendsMetricAccumulatingBucketingStrategy
>;

export const ByMetricTrendsMetricPointInTimeBucketingStrategy = z
  .object({
    type: ByMetricTrendsMetricBucketingStrategyIntersectingType('pointInTime'),
    dateColumn: z.string(),
  })
  .describe('ByMetricTrendsMetricPointInTimeBucketingStrategy');

export type ByMetricTrendsMetricPointInTimeBucketingStrategy = z.infer<
  typeof ByMetricTrendsMetricPointInTimeBucketingStrategy
>;

export const ByMetricTrendsMetricBucketingStrategy = z
  .union([
    ByMetricTrendsMetricAccumulatingBucketingStrategy,
    ByMetricTrendsMetricPointInTimeBucketingStrategy,
  ])
  .describe('ByMetricTrendsMetricBucketingStrategy');

export type ByMetricTrendsMetricBucketingStrategy = z.infer<
  typeof ByMetricTrendsMetricBucketingStrategy
>;

export const ByMetricTrendsMetricConfiguration = z
  .object({
    metric: z.string(),
    availableGroupBys: z.string().array(),
    bucketingStrategy: ByMetricTrendsMetricBucketingStrategy,
    isEnabled: z.boolean().nullable().optional(),
  })
  .describe('ByMetricTrendsMetricConfiguration');

export type ByMetricTrendsMetricConfiguration = z.infer<
  typeof ByMetricTrendsMetricConfiguration
>;

export const ByMetricTrendsEnabledConfiguration = z
  .object({
    isEnabled: z.literal(true),
    isEventStudyAnalysisEnabled: z.boolean().optional(),
    metrics: ByMetricTrendsMetricConfiguration.array(),
    groupByConfigs: ByMetricTrendsGroupByConfiguration.array(),
  })
  .describe('ByMetricTrendsEnabledConfiguration');

export const ByMetricTrendsDisabledConfiguration = z
  .object({
    isEnabled: z.literal(false),
  })
  .describe('ByMetricTrendsDisabledConfiguration');

export const ByMetricTrendsConfig = z
  .union([
    ByMetricTrendsEnabledConfiguration,
    ByMetricTrendsDisabledConfiguration,
  ])
  .describe('ByMetricTrendsConfig');

export type ByMetricTrendsConfig = z.infer<typeof ByMetricTrendsConfig>;
